import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import TopNav from "../../components/navigation/TopNav";
import { useNavigate } from "react-router-dom";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "720px",
  margin: "0 auto",
};

export default function PayerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchWallets = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/payer/available-wallets`);
      setWallets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch wallets", err);
      setWallets([]);
      setMessage("❌ " + t("payer_dashboard.messages.fetch_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchWallets();
  }, []);

  return (
    <StandardScreen
      topNav={<TopNav title={t("payer_dashboard.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("payer_dashboard.title")}>
              <Stack gap="3">
                <p style={{ margin: 0 }}>
                  {t("payer_dashboard.subtitle")}
                </p>

                <StatusMessage
                  message={loading ? t("payer_dashboard.messages.loading") : message}
                />

                {wallets.length > 0 ? (
                  <TableShell maxHeight="60vh">
                    <Table>
                      <thead>
                        <tr>
                          <Th columnIndex={0} sticky>
                            {t("payer_dashboard.table.vendor")}
                          </Th>

                          <Th columnIndex={1} sticky>
                            {t("payer_dashboard.table.route")}
                          </Th>

                          <Th>
                            {t("payer_dashboard.table.wallet")}
                          </Th>

                          <Th align="right">
                            {t("payer_dashboard.table.ticket_balance")}
                          </Th>
                        </tr>
                      </thead>

                      <tbody>
                        {wallets.map((wallet) => (
                          <tr key={wallet.wallet_id}>
                            <Td columnIndex={0} sticky>
                              {wallet.vendor_name || "-"}
                            </Td>

                            <Td columnIndex={1} sticky>
                              {wallet.route_name || "-"}
                            </Td>

                            <Td>
                              {wallet.walletname || "-"}
                            </Td>

                            <NumberTd>{wallet.ticket_balance ?? 0}</NumberTd>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableShell>
                ) : (
                  <div>{t("payer_dashboard.messages.empty")}</div>
                )}
              </Stack>
            </Card>

            <Card>
              <Stack gap="3">
                <h2
                  style={{
                    margin: 0,
                    fontSize: "1.2rem",
                    fontWeight: 700,
                  }}
                >
                  {t("payer_dashboard.frequently_used.title")}
                </h2>

                <p style={{ margin: 0 }}>
                  {t("payer_dashboard.frequently_used.subtitle")}
                </p>

                <Stack gap="2">
                  <Button fullWidth onClick={() => navigate("/payer/pay-for-trip")}>
                    {t("payer_dashboard.frequently_used.label1")}
                  </Button>

                  <Button fullWidth onClick={() => navigate("/payer/to-from-vendors-in-time")}>
                    {t("payer_dashboard.frequently_used.label2")}
                  </Button>

                  <Button fullWidth onClick={() => navigate("/payer/where-is-my-bus")}>
                    {t("payer_dashboard.frequently_used.label3")}
                  </Button>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}